import React, { useEffect, useRef, useCallback, useState } from 'react'
import { connect } from 'react-redux'
import { compose } from 'redux'
import { withRouter } from 'react-router-dom'
import styled from 'styled-components'
import { Modal } from 'antd'
import { segmentApi } from '@edulastic/api'
import {
  IconTestBankLibrary,
  IconContentConverter,
  IconPDLSparkle,
  IconSeeAllArrow,
  IconPlusCircleOutline,
} from '@edulastic/icons'
import { themeColor, greyThemeDark1, white } from '@edulastic/colors'

import ILPDrawer from '../../../AssessmentCreate/components/CreateAITest/ILPDrawer'
import UnVerifiedUserWarningModal from '../../../../common/components/UnVerifiedUserWarningModal'
import { CREATE_TEST_SEGMENT_EVENT_NAME } from '../../../AssessmentCreate/components/CreationOptions/constants'
import {
  getInterestedGradesSelector,
  getInterestedSubjectsSelector,
  isContentConvertEnabledSelector,
} from '../../../src/selectors/user'
import {
  getEmailVerified,
  getVerificationTS,
  isDefaultDASelector,
  isSignupUsingUNAndPassSelector,
} from '../../../../student/Login/ducks'
import BulkConvertDrawer from '../../../AssessmentCreate/components/CreateAITest/BulkConvertDrawer'

const CREATE_TEST_MODAL_ROUTES = {
  Convert: '/author/tests/import',
  SearchTest: '/author/tests',
  BuildTest: '/author/tests/create',
}

const CreateTestModal = ({
  visible,
  onClose,
  history,
  triggerButtonRef,
  defaultGrades,
  defaultSubjects,
  emailVerified,
  verificationTS,
  isDefaultDA,
  isSignupUsingUNAndPass,
  isContentConvertEnabled,
}) => {
  const modalContentRef = useRef(null)
  const firstOptionRef = useRef(null)
  const [isILPDrawerOpen, setIsILPDrawerOpen] = useState(false)
  const [isILPVisible, setIsILPVisible] = useState(false)
  const [isBulkConvertVisible, setIsBulkConvertVisible] = useState(false)

  const closeBulkConvertDrawer = () => {
    setIsBulkConvertVisible(false)
  }

  const [
    showUnVerifiedUserWarningModal,
    setShowUnVerifiedUserWarningModal,
  ] = useState(false)

  const checkEmailVerification = useCallback(() => {
    if (
      !emailVerified &&
      (verificationTS || isSignupUsingUNAndPass) &&
      !isDefaultDA
    ) {
      onClose() // Close the CreateTestModal first
      setShowUnVerifiedUserWarningModal(true)
      return true
    }
    return false
  }, [
    emailVerified,
    verificationTS,
    isSignupUsingUNAndPass,
    isDefaultDA,
    onClose,
  ])
  // Check if user needs email verification before opening ILP
  const handleGenerateWithAI = () => {
    // Track telemetry - use existing AI test event
    segmentApi.genericEventTrack('AIQuizCreateTestClick', {
      source: 'Dashboard',
    })

    if (checkEmailVerification()) {
      return
    }
    // Open PDL drawer and close modal
    setIsILPVisible(true)
    onClose()
  }

  const handleContentConvert = () => {
    // Track telemetry - use existing AI test event
    segmentApi.genericEventTrack(
      CREATE_TEST_SEGMENT_EVENT_NAME.TEST_IMPORT_CLICK,
      {
        source: 'Dashboard - Create Test Modal',
      }
    )

    if (checkEmailVerification()) {
      return
    }

    // Open Bulk Convert drawer and close modal
    setIsBulkConvertVisible(true)
    onClose()
  }

  // Focus first option when modal opens
  useEffect(() => {
    if (visible) {
      setTimeout(() => {
        firstOptionRef.current?.focus()
      }, 100)
    }
  }, [visible])

  // Handle Escape key
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        onClose()
      }
      // Focus trap implementation
      if (e.key === 'Tab' && modalContentRef.current) {
        const focusableElements = modalContentRef.current.querySelectorAll(
          'button, [href], [tabindex]:not([tabindex="-1"])'
        )
        const firstElement = focusableElements[0]
        const lastElement = focusableElements[focusableElements.length - 1]

        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault()
          lastElement?.focus()
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault()
          firstElement?.focus()
        }
      }
    },
    [onClose]
  )

  useEffect(() => {
    if (visible) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [visible, handleKeyDown])

  // Return focus to trigger button when modal closes
  const handleClose = useCallback(() => {
    // Don't close if ILP drawer is open
    if (isILPDrawerOpen) {
      return
    }
    onClose()
    setTimeout(() => {
      triggerButtonRef?.current?.focus()
    }, 100)
  }, [onClose, triggerButtonRef, isILPDrawerOpen])

  // Sync isILPDrawerOpen with isILPVisible
  useEffect(() => {
    setIsILPDrawerOpen(isILPVisible)
  }, [isILPVisible])

  // Reset ILP drawer open flag when modal closes (but not isILPVisible - drawer manages itself)
  useEffect(() => {
    if (!visible) {
      setIsILPDrawerOpen(false)
    }
  }, [visible])

  const handleOptionClick = (optionKey) => {
    // Track telemetry using old dropdown events for consistency
    switch (optionKey) {
      case 'Convert':
        segmentApi.genericEventTrack(
          CREATE_TEST_SEGMENT_EVENT_NAME.TEST_IMPORT_CLICK,
          {
            source: 'Dashboard - Create Test Modal',
          }
        )
        break
      case 'SearchTest':
        segmentApi.genericEventTrack('CreateMenu:TestLibrary', {
          source: 'Dashboard',
        })
        break
      case 'BuildTest':
        segmentApi.genericEventTrack('CreateMenu:Scratch', {
          source: 'Dashboard',
        })
        break
      default:
        break
    }

    handleClose()

    if (optionKey === 'Convert') {
      if (!isContentConvertEnabled) {
        history.push(CREATE_TEST_MODAL_ROUTES.Convert)
      }
    } else {
      history.push(CREATE_TEST_MODAL_ROUTES[optionKey])
    }
  }

  const handleSeeAllMethods = useCallback(() => {
    segmentApi.genericEventTrack('CreateMenu:SeeAll', {
      source: 'Dashboard',
    })
    handleClose()
    history.push('/author/tests/select')
  }, [history, handleClose])

  return (
    <>
      <StyledModal
        visible={visible}
        onCancel={handleClose}
        footer={null}
        centered
        destroyOnClose={!isILPDrawerOpen}
        maskClosable={!isILPDrawerOpen}
        title={null}
        closable={!isILPDrawerOpen}
        width={584}
        data-cy="createTestModal"
      >
        <ModalContent
          ref={modalContentRef}
          role="dialog"
          aria-label="Create a test"
        >
          <ModalHeader>
            <ModalTitle>Create a test</ModalTitle>
          </ModalHeader>
          <OptionsGrid>
            {/* Convert a document */}
            <OptionCard
              ref={firstOptionRef}
              onClick={() => {
                if (isContentConvertEnabled) {
                  handleContentConvert()
                } else {
                  handleOptionClick('Convert')
                }
              }}
              tabIndex={0}
              role="button"
              aria-label="Convert a document: Convert a document or Google Form into a test"
              data-cy="createTestModalConvert"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  if (isContentConvertEnabled) {
                    handleContentConvert()
                  } else {
                    handleOptionClick('Convert')
                  }
                }
              }}
            >
              <ConvertIconWrapper>
                <IconContentConverter
                  aria-hidden="true"
                  width="40px"
                  height="40px"
                />
              </ConvertIconWrapper>
              <OptionTitle>Content Converter</OptionTitle>
              <OptionDescription>
                Convert a document or Google Form into a test
              </OptionDescription>
            </OptionCard>

            {/* Generate with AI */}
            <OptionCard
              tabIndex={0}
              role="button"
              aria-label="Generate with AI: Generate from a prompt, link, or standards"
              data-cy="createTestModalGenai"
              onClick={handleGenerateWithAI}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleGenerateWithAI()
                }
              }}
            >
              <AIIconWrapper>
                <IconPDLSparkle aria-hidden="true" width="40px" height="40px" />
              </AIIconWrapper>
              <OptionTitle>Generate with AI</OptionTitle>
              <OptionDescription>
                Generate from a prompt, link, or standards
              </OptionDescription>
            </OptionCard>

            {/* Browse the Test Bank */}
            <OptionCard
              onClick={() => handleOptionClick('SearchTest')}
              tabIndex={0}
              role="button"
              aria-label="Browse the Test Bank: Explore 400k educator-created tests"
              data-cy="createTestModalSearchTest"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleOptionClick('SearchTest')
                }
              }}
            >
              <TestBankIconWrapper>
                <IconTestBankLibrary
                  aria-hidden="true"
                  width="36px"
                  height="36px"
                />
              </TestBankIconWrapper>
              <OptionTitle>Browse the Test Bank</OptionTitle>
              <OptionDescription>
                Explore 400k educator-created tests
              </OptionDescription>
            </OptionCard>

            {/* Build manually */}
            <OptionCard
              onClick={() => handleOptionClick('BuildTest')}
              tabIndex={0}
              role="button"
              aria-label="Build manually: Choose from the Item Bank or author with 50+ question types"
              data-cy="createTestModalBuildTest"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleOptionClick('BuildTest')
                }
              }}
            >
              <IconPlusCircleOutline
                aria-hidden="true"
                width="34px"
                height="34px"
              />
              <OptionTitle>Build manually</OptionTitle>
              <OptionDescription>
                Choose from the Item Bank or author with 50+ question types
              </OptionDescription>
            </OptionCard>
          </OptionsGrid>
          <SeeAllMethodsLink
            onClick={handleSeeAllMethods}
            tabIndex={0}
            role="button"
            aria-label="See all methods"
            data-cy="createTestModalSeeAll"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleSeeAllMethods()
              }
            }}
          >
            See all methods <IconSeeAllArrow aria-hidden="true" />
          </SeeAllMethodsLink>
        </ModalContent>
      </StyledModal>
      {showUnVerifiedUserWarningModal && (
        <UnVerifiedUserWarningModal
          visible={showUnVerifiedUserWarningModal}
          onCloseModal={() => setShowUnVerifiedUserWarningModal(false)}
          closeButtonText="Go Back"
        />
      )}
      {isBulkConvertVisible && (
        <BulkConvertDrawer
          visible={isBulkConvertVisible}
          onClose={() => closeBulkConvertDrawer()}
          source="createTestModal"
        />
      )}
      {isILPVisible && (
        <ILPDrawer
          visible={isILPVisible}
          setIsILPVisible={setIsILPVisible}
          showILPProductSelectionScreen={false}
          defaultGrades={defaultGrades}
          defaultSubjects={defaultSubjects}
        />
      )}
    </>
  )
}

const mapStateToProps = (state) => ({
  defaultGrades: getInterestedGradesSelector(state),
  defaultSubjects: getInterestedSubjectsSelector(state),
  emailVerified: getEmailVerified(state),
  verificationTS: getVerificationTS(state),
  isDefaultDA: isDefaultDASelector(state),
  isSignupUsingUNAndPass: isSignupUsingUNAndPassSelector(state),
  isContentConvertEnabled: isContentConvertEnabledSelector(state),
})

const enhance = compose(withRouter, connect(mapStateToProps, null))

export default enhance(CreateTestModal)

// Styled Components
const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0px 0px 17px 0px rgba(0, 0, 0, 0.14);
  }

  .ant-modal-header {
    display: none;
  }

  .ant-modal-body {
    padding: 0;
  }

  .ant-modal-close {
    top: 20px;
    right: 28px;

    .ant-modal-close-x {
      width: 24px;
      height: 24px;
      line-height: 24px;
      font-size: 16px;
      color: ${greyThemeDark1};

      &:hover {
        color: ${themeColor};
      }
    }
  }
`

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 27px;
  padding: 24px 32px 32px;
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`

const ModalTitle = styled.h2`
  margin: 0;
  font-family: 'Open Sans', sans-serif;
  font-size: 22px;
  font-weight: 700;
  line-height: 30px;
  color: #151d34;
`

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
`

const OptionCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 24px;
  min-height: 168px;
  background: ${white};
  border: 2px solid #d9d9d9;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover,
  &:focus {
    border-color: ${themeColor};
    background-color: #f0f7ff;
    outline: none;
  }

  &:focus {
    box-shadow: 0 0 0 2px ${themeColor}40;
  }
`

// Convert icon
const ConvertIconWrapper = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  flex-shrink: 0;

  svg {
    color: #151d34;
    fill: #151d34;
  }
`

// AI icon - sparkle with its natural lime/yellow color, no background
const AIIconWrapper = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  flex-shrink: 0;
`

// Test Bank icon - dark icon, no background
const TestBankIconWrapper = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  flex-shrink: 0;

  svg {
    color: #151d34;
    fill: #151d34;
  }
`

const OptionTitle = styled.div`
  font-family: 'Open Sans', sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.2;
  color: #151d34;
`

const OptionDescription = styled.div`
  font-family: 'Open Sans', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  color: #151d34;
`

const SeeAllMethodsLink = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  margin-top: 8px;
  border-top: 1px solid #e8e8e8;
  cursor: pointer;
  font-family: 'Open Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${themeColor};
  transition: all 0.2s ease;

  svg {
    fill: ${themeColor};
    transition: fill 0.2s ease;
  }

  &:hover {
    color: ${themeColor};
    background-color: rgba(0, 0, 0, 0.02);
  }

  &:focus {
    outline: 2px solid ${themeColor};
    outline-offset: -2px;
    border-radius: 4px;
  }
`
